/**
 * Zod schemas derived from the FastAPI OpenAPI snapshot at
 * http://127.0.0.1:8091/openapi.json (captured 2026-05-17).
 *
 * These are the runtime source of truth for all API request/response shapes.
 * TypeScript types are inferred via z.infer<typeof X> — no parallel hand-written
 * interfaces — so a schema drift will surface as both a runtime parse error and
 * a compile-time type mismatch.
 *
 * If the backend changes shape, re-snapshot the OpenAPI and update here.
 */

import { z } from "zod";

/* ---------------------------------------------------------------- enums */

export const ProjectMode = z.enum(["prompt_only", "full_pipeline"]);
export type ProjectMode = z.infer<typeof ProjectMode>;

export const AssetInferenceMode = z.enum(["creative", "explicit_only"]);
export type AssetInferenceMode = z.infer<typeof AssetInferenceMode>;

/* ---------------------------------------------------------------- auth */

export const UserPublic = z.object({
  id: z.number().int(),
  email: z.string(),
  display_name: z.string(),
  created_at: z.string(),
});
export type UserPublic = z.infer<typeof UserPublic>;

export const AuthMeResponse = z.object({
  user: UserPublic,
});
export type AuthMeResponse = z.infer<typeof AuthMeResponse>;

export const SignupRequest = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
  display_name: z.string().max(200).default(""),
});
export type SignupRequest = z.infer<typeof SignupRequest>;

export const LoginRequest = z.object({
  email: z.string().email(),
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const CharacterStatus = z.enum(["draft", "ready"]);
export type CharacterStatus = z.infer<typeof CharacterStatus>;

export const ShotStatus = z.enum([
  "draft",
  "prompt_ready",
  "keyframe_pending",
  "keyframe_ready",
  "video_pending",
  "video_ready",
  "failed",
]);
export type ShotStatus = z.infer<typeof ShotStatus>;

/* ---------------------------------------------------------------- projects */

export const ProjectRead = z.object({
  id: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  direction_style: z.string().nullable(),
  visual_style: z.string().nullable().optional(),
  aspect_ratio: z.string(),
  default_resolution: z.string(),
  llm_token_budget: z.number().int(),
  media_credit_budget: z.number().int(),
  mode: ProjectMode,
  // Defaults to "creative" so a pre-this-feature API response (no field on the
  // wire) parses cleanly — matches the backend column server_default.
  asset_inference_mode: AssetInferenceMode.default("creative"),
  default_color_grade: z.string().optional().nullable(),
  default_lighting_setup: z.string().optional().nullable(),
  image_model: z.string().nullable().optional(),
  // B2: one-button generation status fields — default "idle" so older API
  // responses (pre-B2 backend) parse cleanly without a zod failure.
  generation_status: z.string().default("idle"),
  generation_step: z.string().nullable().optional(),
  generation_error: z.string().nullable().optional(),
});
export type ProjectRead = z.infer<typeof ProjectRead>;

export const ProjectCreate = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional().nullable(),
  direction_style: z.string().optional().nullable(),
  visual_style: z.string().optional().nullable(),
  aspect_ratio: z.string().default("16:9"),
  default_resolution: z.string().default("720p"),
  llm_token_budget: z.number().int().min(0).default(2_000_000),
  media_credit_budget: z.number().int().min(0).default(10_000),
  mode: ProjectMode.default("prompt_only"),
  image_model: z.string().nullable().optional(),
});
export type ProjectCreate = z.infer<typeof ProjectCreate>;

export const ProjectUpdate = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().nullable().optional(),
  direction_style: z.string().nullable().optional(),
  visual_style: z.string().nullable().optional(),
  aspect_ratio: z.string().optional(),
  default_resolution: z.string().optional(),
  llm_token_budget: z.number().int().min(0).optional(),
  media_credit_budget: z.number().int().min(0).optional(),
  // mode is editable so users can switch a project between prompt_only
  // (LLM helpers + manual export) and full_pipeline (image-gen unlocked).
  mode: ProjectMode.optional(),
  asset_inference_mode: AssetInferenceMode.optional(),
  default_color_grade: z.string().nullable().optional(),
  default_lighting_setup: z.string().nullable().optional(),
  image_model: z.string().nullable().optional(),
});
export type ProjectUpdate = z.infer<typeof ProjectUpdate>;

/* ---------------------------------------------------------------- characters */

export const CharacterRead = z.object({
  id: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
  project_id: z.number().int(),
  name: z.string(),
  base_description: z.string(),
  base_image_path: z.string().nullable(),
  status: CharacterStatus,
  // Optional structured inputs added by Path A (Stage 2). Null for characters
  // created before Path A or via the legacy free-text flow.
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type CharacterRead = z.infer<typeof CharacterRead>;

export const CharacterCreate = z.object({
  name: z.string().min(1).max(200),
  base_description: z.string().min(1),
  status: CharacterStatus.default("draft"),
});
export type CharacterCreate = z.infer<typeof CharacterCreate>;

export const CharacterUpdate = z.object({
  name: z.string().min(1).max(200).optional(),
  base_description: z.string().min(1).optional(),
  status: CharacterStatus.optional(),
  // Structured inputs — written by Builder mode; opaque dict, validated at
  // the from-seed endpoint. PATCH in Custom mode omits this field entirely
  // (undefined, not null) so existing prompt_inputs are preserved as backup.
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type CharacterUpdate = z.infer<typeof CharacterUpdate>;

/* ---------------------------------------------------------------- costumes */

export const CostumeRead = z.object({
  id: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
  project_id: z.number().int(),
  character_id: z.number().int().nullable(),
  name: z.string(),
  description: z.string(),
  reference_image_path: z.string().nullable(),
  // Optional structured inputs added by Path A (Stage 6). Null for costumes
  // created before Path A or via the legacy free-text flow.
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type CostumeRead = z.infer<typeof CostumeRead>;

export const CostumeCreate = z.object({
  name: z.string().min(1).max(200),
  description: z.string().min(1),
  character_id: z.number().int().nullable().optional(),
});
export type CostumeCreate = z.infer<typeof CostumeCreate>;

export const CostumeUpdate = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().min(1).optional(),
  character_id: z.number().int().nullable().optional(),
  // Structured inputs — written by Builder mode; opaque dict, validated at
  // the from-seed endpoint. PATCH in Custom mode omits this field entirely
  // (undefined, not null) so existing prompt_inputs are preserved as backup.
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type CostumeUpdate = z.infer<typeof CostumeUpdate>;

/* ---------------------------------------------------------------- locations */

export const LocationRead = z.object({
  id: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
  project_id: z.number().int(),
  name: z.string(),
  description: z.string(),
  lighting_notes: z.string().nullable(),
  reference_image_path: z.string().nullable(),
  // Optional structured inputs added by Path A (Stage 6).
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type LocationRead = z.infer<typeof LocationRead>;

export const LocationCreate = z.object({
  name: z.string().min(1).max(200),
  description: z.string().min(1),
  lighting_notes: z.string().nullable().optional(),
});
export type LocationCreate = z.infer<typeof LocationCreate>;

export const LocationUpdate = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().min(1).optional(),
  lighting_notes: z.string().nullable().optional(),
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type LocationUpdate = z.infer<typeof LocationUpdate>;

/* ---------------------------------------------------------------- props */

export const PropRead = z.object({
  id: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
  project_id: z.number().int(),
  name: z.string(),
  description: z.string(),
  reference_image_path: z.string().nullable(),
  // Optional structured inputs added by Path A (Stage 6).
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type PropRead = z.infer<typeof PropRead>;

export const PropCreate = z.object({
  name: z.string().min(1).max(200),
  description: z.string().min(1),
});
export type PropCreate = z.infer<typeof PropCreate>;

export const PropUpdate = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().min(1).optional(),
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type PropUpdate = z.infer<typeof PropUpdate>;

/* ---------------------------------------------------------------- scenes */

export const SceneRead = z.object({
  id: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
  project_id: z.number().int(),
  ordinal: z.number().int(),
  name: z.string(),
  narrative: z.string().nullable(),
  location_id: z.number().int().nullable(),
  music_notes: z.string().nullable(),
  assembled_video_path: z.string().nullable(),
  thumbnail_path: z.string().nullable(),
  grid_image_path: z.string().nullable().optional(),
});
export type SceneRead = z.infer<typeof SceneRead>;

export const SceneCreate = z.object({
  ordinal: z.number().int().min(0),
  name: z.string().min(1).max(200),
  narrative: z.string().nullable().optional(),
  location_id: z.number().int().nullable().optional(),
  music_notes: z.string().nullable().optional(),
});
export type SceneCreate = z.infer<typeof SceneCreate>;

export const SceneUpdate = z.object({
  ordinal: z.number().int().min(0).optional(),
  name: z.string().min(1).max(200).optional(),
  narrative: z.string().nullable().optional(),
  location_id: z.number().int().nullable().optional(),
  music_notes: z.string().nullable().optional(),
});
export type SceneUpdate = z.infer<typeof SceneUpdate>;

/* ---------------------------------------------------------------- shots */

export const ShotRead = z.object({
  id: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
  scene_id: z.number().int(),
  ordinal: z.number().int(),
  shot_number: z.string(),
  raw_description: z.string(),
  enhanced_prompt: z.string().nullable(),
  seedance_prompt_text: z.string().nullable(),
  character_id: z.number().int().nullable(),
  // Full ordered set of characters in this shot (backlog #4 Phase 1).
  // When character_id is null (screenplay-generated shot with no assigned
  // character yet) this array is also empty. Readers must filter out nulls
  // and tolerate an empty list.
  character_ids: z.array(z.number().int()).default([]),
  costume_id_override: z.number().int().nullable(),
  location_id_override: z.number().int().nullable(),
  character_description_override: z.string().nullable().default(null),
  duration_seconds: z.number().int(),
  beat_notes: z.string().nullable(),
  keyframe_image_path: z.string().nullable(),
  video_path: z.string().nullable(),
  status: ShotStatus,
  shot_type: z.string().optional().nullable(),
  camera_movement: z.string().optional().nullable(),
  // Structured inputs from the Director's brief panel (Stage 10+).
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type ShotRead = z.infer<typeof ShotRead>;

export const ShotCreate = z.object({
  ordinal: z.number().int().min(0),
  shot_number: z.string().min(1).max(32),
  raw_description: z.string().min(1),
  character_id: z.number().int(),
  duration_seconds: z.number().int().min(4).max(15),
  beat_notes: z.string().nullable().optional(),
});
export type ShotCreate = z.infer<typeof ShotCreate>;

/**
 * Partial update for a shot — mirrors backend `ShotUpdate` (extra="forbid").
 *
 * `character_description_override` semantics (per spec §10):
 *   - omitted / `undefined`  → field unchanged
 *   - `null`                 → clear the override (inherit from canonical)
 *   - any string (incl. "")  → explicit override; "" is a valid empty override
 *
 * All other fields are optional patches. Nullable fields that exist on the
 * backend as `Optional[...]` are modeled as `.nullable().optional()` so that
 * `null` is the explicit-clear signal and `undefined` is "leave alone".
 */
export const ShotUpdate = z.object({
  ordinal: z.number().int().min(0).optional(),
  shot_number: z.string().min(1).max(32).optional(),
  raw_description: z.string().optional(),
  enhanced_prompt: z.string().nullable().optional(),
  seedance_prompt_text: z.string().nullable().optional(),
  character_description_override: z.string().nullable().optional(),
  character_id: z.number().int().optional(),
  // Full replacement for the character set when present. Must be non-empty;
  // first element becomes the primary (mirrored into character_id).
  character_ids: z.array(z.number().int()).min(1).optional(),
  duration_seconds: z.number().int().min(4).max(15).optional(),
  costume_id_override: z.number().int().nullable().optional(),
  location_id_override: z.number().int().nullable().optional(),
  beat_notes: z.string().nullable().optional(),
  status: ShotStatus.optional(),
  shot_type: z.string().nullable().optional(),
  camera_movement: z.string().nullable().optional(),
  // Structured inputs from the Director's brief panel (Stage 10+).
  prompt_inputs: z.record(z.unknown()).nullable().optional(),
});
export type ShotUpdate = z.infer<typeof ShotUpdate>;

/* ---------------------------------------------------------------- breakdown */

export const BreakdownResponse = z.object({
  scene_id: z.number().int(),
  shots: z.array(ShotRead),
});
export type BreakdownResponse = z.infer<typeof BreakdownResponse>;

/* ---------------------------------------------------------------- prompt manifest */

export const DialogueLine = z.object({
  character: z.string().nullable(),
  parenthetical: z.string().nullable(),
  text: z.string(),
});
export type DialogueLine = z.infer<typeof DialogueLine>;

export const ExportShot = z.object({
  id: z.number().int(),
  ordinal: z.number().int(),
  shot_number: z.string(),
  raw_description: z.string(),
  enhanced_prompt: z.string().nullable(),
  seedance_prompt_text: z.string().nullable(),
  duration_seconds: z.number().int(),
  character_name: z.string(),
  costume_override_name: z.string().nullable(),
  location_override_name: z.string().nullable(),
  beat_notes: z.string().nullable(),
  // Added by a parallel backend change. Default to [] so the UI keeps working
  // both pre- and post-deploy of that change.
  props: z.array(z.string()).default([]),
  // WS-5: new consistency + media fields (all nullable/optional so the frontend
  // keeps working both before and after the backend deploys these fields).
  character_names: z.array(z.string()).optional(),
  keyframe_image_path: z.string().nullable().optional(),
  start_frame_image_path: z.string().nullable().optional(),
  character_ref_image_paths: z.array(z.string()).optional(),
  location_ref_image_path: z.string().nullable().optional(),
  costume_ref_image_path: z.string().nullable().optional(),
  prop_ref_image_paths: z.array(z.string()).optional(),
  // Dialogue lines extracted from screenplay (nullable/optional for back-compat
  // with deploys that predate the dialogue preservation feature).
  dialogue_lines: z.array(DialogueLine).nullable().optional(),
});
export type ExportShot = z.infer<typeof ExportShot>;

export const ExportScene = z.object({
  id: z.number().int(),
  ordinal: z.number().int(),
  name: z.string(),
  narrative: z.string().nullable(),
  location_name: z.string().nullable(),
  shots: z.array(ExportShot),
});
export type ExportScene = z.infer<typeof ExportScene>;

export const ExportProject = z.object({
  id: z.number().int(),
  name: z.string(),
  mode: z.string(),
  direction_style: z.string().nullable(),
  aspect_ratio: z.string(),
  // Backend always returns these (defaults to NULL when unset). Optional
  // on the wire only because old deploys didn't include them.
  color_grade: z.string().nullable().optional(),
  lighting: z.string().nullable().optional(),
  visual_style: z.string().nullable().optional(),
});
export type ExportProject = z.infer<typeof ExportProject>;

export const PromptManifest = z.object({
  project: ExportProject,
  scenes: z.array(ExportScene),
});
export type PromptManifest = z.infer<typeof PromptManifest>;

/* ---------------------------------------------------------------- settings (read-only) */

export const AdapterName = z.enum(["manual", "naga"]);
export type AdapterName = z.infer<typeof AdapterName>;

export const AdapterInfo = z.object({
  name: AdapterName,
  model: z.string().nullable(),
});
export type AdapterInfo = z.infer<typeof AdapterInfo>;

export const AvailableImageModels = z.object({
  chat_endpoint: z.array(z.string()),
  images_endpoint: z.array(z.string()),
  openrouter_endpoint: z.array(z.string()),
});
export type AvailableImageModels = z.infer<typeof AvailableImageModels>;

export const AdaptersResponse = z.object({
  image: AdapterInfo,
  video: AdapterInfo,
  available_image_models: AvailableImageModels,
});
export type AdaptersResponse = z.infer<typeof AdaptersResponse>;

/* ---------------------------------------------------------------- generation */

export const GenerationResult = z.object({
  provider_job_id: z.string(),
  model: z.string(),
  prompt: z.string(),
  image_path: z.string(),
});
export type GenerationResult = z.infer<typeof GenerationResult>;

/** Read-only storyboard prompt for a scene (GET /api/scenes/:id/grid-prompt). */
export const GridPrompt = z.object({
  prompt: z.string(),
  shot_count: z.number().int(),
});
export type GridPrompt = z.infer<typeof GridPrompt>;

export const AssetReferenceImageRead = z.object({
  id: z.string(),
  asset_kind: z.enum(["character", "costume", "location", "prop"]),
  asset_id: z.number().int(),
  ordinal: z.number().int().min(0).max(3),
  image_path: z.string(),
  kind: z.string().nullable(),
  source: z.enum(["uploaded", "generated"]),
  provider_job_id: z.string().nullable(),
  created_at: z.string(),
});
export type AssetReferenceImageRead = z.infer<typeof AssetReferenceImageRead>;

/* ---------------------------------------------------------------- user drafts */

export const UserDraftRead = z.object({
  id: z.number().int(),
  user_id: z.number().int(),
  kind: z.string(),
  payload_json: z.record(z.unknown()),
  created_at: z.string(),
  updated_at: z.string(),
});
export type UserDraftRead = z.infer<typeof UserDraftRead>;

export const UserDraftUpsert = z.object({
  payload_json: z.record(z.unknown()),
});
export type UserDraftUpsert = z.infer<typeof UserDraftUpsert>;

/* ---------------------------------------------------------------- screenplays */

export const ScreenplayRead = z.object({
  id: z.number().int(),
  user_id: z.number().int(),
  project_id: z.number().int(),
  title: z.string(),
  doc_json: z.record(z.string(), z.unknown()),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ScreenplayRead = z.infer<typeof ScreenplayRead>;

export const ScreenplayUpdate = z.object({
  title: z.string().min(1).max(200).optional(),
  doc_json: z.record(z.string(), z.unknown()).optional(),
});
export type ScreenplayUpdate = z.infer<typeof ScreenplayUpdate>;

export const RedoProjectResponse = z.object({
  status: z.string(),
  project_id: z.number().int(),
});
export type RedoProjectResponse = z.infer<typeof RedoProjectResponse>;

/* ---------------------------------------------------------------- errors */

export const ValidationError = z.object({
  loc: z.array(z.union([z.string(), z.number()])),
  msg: z.string(),
  type: z.string(),
});

export const HTTPValidationError = z.object({
  detail: z.array(ValidationError).optional(),
});
